/**
 * Web-specific implementation of the fretboard dimensions hook.
 * Uses DOM APIs for measurements and window resize events.
 */

import { createFretboardDimensionsHook } from '@fretgenius/ui';
import { useFretboardConfigStore } from '../stores/useFretboardConfigStore';

/**
 * Web implementation of the fretboard dimensions hook.
 * Uses DOM APIs for measurement and window resize events.
 *
 * Creates a hook compatible with web environment that uses:
 * - clientWidth for container width
 * - getBoundingClientRect for available height
 * - window resize event for updates
 */
export const useFretboardDimensions = createFretboardDimensionsHook<{
  current: HTMLDivElement | null;
}>(
  {
    // Get container width from DOM element
    getContainerWidth: (containerRef) => {
      return containerRef?.current?.clientWidth || 0;
    },

    // Get available height from container if available, otherwise from window
    getAvailableHeight: (containerRef) => {
      if (containerRef?.current) {
        const rect = containerRef.current.getBoundingClientRect();
        return window.innerHeight - rect.top;
      }
      return window.innerHeight * 0.5;
    },

    // Set up resize handling with debounce
    setupResizeHandling: (calculateDimensions) => {
      let resizeTimeout: ReturnType<typeof setTimeout> | null = null;

      const handleResize = () => {
        if (resizeTimeout) {
          clearTimeout(resizeTimeout);
        }

        resizeTimeout = setTimeout(() => {
          calculateDimensions();
        }, 200); // 200ms debounce
      };

      window.addEventListener('resize', handleResize);

      // Trigger initial calculation
      calculateDimensions();

      // Cleanup function
      return () => {
        window.removeEventListener('resize', handleResize);
        if (resizeTimeout) {
          clearTimeout(resizeTimeout);
        }
      };
    },
  },
  useFretboardConfigStore
);
